import type { GalleryImageT, GalleryLayoutT } from "./gallery-node-extension";

export type { GalleryLayoutT } from "./gallery-node-extension";

export const isGalleryLayout = (value: string): value is GalleryLayoutT => {
  return value === "hero" || value === "grid" || value === "overlay";
};

export const DEFAULT_GALLERY_LAYOUT: GalleryLayoutT = "hero";

/**
 * Intranet images live in private buckets behind an auth proxy
 * (/api/intranet/image, /api/intranet/wiki-image) that redirects to a
 * signed URL. next/image cannot optimize those (query string, redirect),
 * so they must be rendered unoptimized.
 */
export const INTRANET_IMAGE_PROXY_PREFIX = "/api/intranet/";

export const isUnoptimizedImageSrc = (src: string): boolean =>
  src.startsWith(INTRANET_IMAGE_PROXY_PREFIX) ||
  src.startsWith("blob:") ||
  src.startsWith("data:");

export const GALLERY_MAX_IMAGES: Record<GalleryLayoutT, number> = {
  hero: 7,
  grid: 8,
  overlay: 5,
};

export type GalleryHeroLayoutItemT = {
  image: GalleryImageT;
  itemType: "primary" | "secondary";
  showRemainingOverlay: boolean;
};

export type GalleryGridLayoutItemT = {
  image: GalleryImageT;
  showRemainingOverlay: boolean;
};

export type GalleryOverlayLayoutT = {
  mainImage: GalleryImageT;
  thumbnails: GalleryImageT[];
  remainingCount: number;
};

export type GalleryLayoutDataT =
  | {
      type: "hero";
      items: GalleryHeroLayoutItemT[];
      remainingCount: number;
      isSingleImage: boolean;
    }
  | {
      type: "grid";
      items: GalleryGridLayoutItemT[];
      remainingCount: number;
    }
  | ({
      type: "overlay";
    } & GalleryOverlayLayoutT)
  | {
      type: "empty";
    };

export const getMaxImagesForLayout = (layout: GalleryLayoutT): number => {
  return GALLERY_MAX_IMAGES[layout] ?? GALLERY_MAX_IMAGES.grid;
};

const getDisplayImages = (
  images: GalleryImageT[],
  layout: GalleryLayoutT
): {
  displayImages: GalleryImageT[];
  remainingCount: number;
  maxImages: number;
} => {
  const maxImages = getMaxImagesForLayout(layout);
  const displayImages = images.slice(0, maxImages);
  const remainingCount = Math.max(0, images.length - maxImages);

  return { displayImages, remainingCount, maxImages };
};

export const getGalleryLayoutData = (
  images: GalleryImageT[],
  layout: GalleryLayoutT
): GalleryLayoutDataT => {
  if (!images.length) {
    return { type: "empty" };
  }

  if (layout === "hero") {
    const { displayImages, remainingCount, maxImages } = getDisplayImages(
      images,
      layout
    );

    const items: GalleryHeroLayoutItemT[] = displayImages.map(
      (image, index) => ({
        image,
        itemType: index === 0 ? "primary" : "secondary",
        showRemainingOverlay: index === maxImages - 1 && remainingCount > 0,
      })
    );

    return {
      type: "hero",
      items,
      remainingCount,
      isSingleImage: images.length === 1,
    };
  }

  if (layout === "grid") {
    const { displayImages, remainingCount, maxImages } = getDisplayImages(
      images,
      layout
    );

    const items: GalleryGridLayoutItemT[] = displayImages.map(
      (image, index) => ({
        image,
        showRemainingOverlay: index === maxImages - 1 && remainingCount > 0,
      })
    );

    return {
      type: "grid",
      items,
      remainingCount,
    };
  }

  const { displayImages, remainingCount } = getDisplayImages(images, layout);
  const mainImage = displayImages[0];
  const thumbnails = displayImages.slice(1);

  if (!mainImage) {
    return { type: "empty" };
  }

  return {
    type: "overlay",
    mainImage,
    thumbnails,
    remainingCount,
  };
};
